"use client";

import React from "react";

/**
 * Goal icon - soccer ball with optional count
 */
export const GoalIcon: React.FC<{ count?: number; className?: string }> = ({ count, className = "" }) => (
    <span className={`inline-flex items-center bg-white rounded-full shadow-sm  ${className}`}>
        <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(.63)" fill="none" fillRule="evenodd">
                <circle cx="7" cy="7" r="6" fill="#FDFDFD" />
                <path
                    fill="#222"
                    d="M7 0h.28H7a6.8 6.8 0 01.56.02l.14.01.25.03h.02l.04.01.22.04.13.02.14.03.13.03.24.06.08.03.23.07.04.01.53.2.03.01c.95.42 1.8 1.03 2.47 1.8a5.96 5.96 0 01.62.82l.08.13.06.1c.42.7.72 1.49.87 2.32l.02.07a7.47 7.47 0 01.07 1.85 6.97 6.97 0 01-.94 2.9l-.03.05-.08.13.1-.18-.2.34-.03.04-.37.5-.04.03c-.12.16-.26.3-.4.45l-.05.05-.12.12-.4.35-.07.05-.19.15-.04.02v.01l-.25.17.25-.18a7.3 7.3 0 01-1.67.9l-.05.02-.22.08-.06.02a5.9 5.9 0 01-.37.1l-.16.04a6.95 6.95 0 01-3.11.01l-.06-.01-.15-.04-.09-.02-.03-.01a6.16 6.16 0 01-.24-.07l-.09-.03-.2-.07-.06-.02a7.96 7.96 0 01-.24-.1h-.03c-.5-.22-.96-.48-1.38-.79h-.01l-.04-.03-.2-.16.24.18a6.66 6.66 0 01-.82-.7l-.05-.04a6.47 6.47 0 01-.4-.45l-.04-.04A6.97 6.97 0 01.03 7.66a7.5 7.5 0 010-1.34l.02-.13.01-.11.04-.27.02-.11c.16-.82.45-1.59.87-2.28l.06-.1.08-.13A6.94 6.94 0 014.22.58l.04-.02.51-.2.06-.01.23-.08.06-.01.25-.07c.05 0 .09-.02.13-.03l.14-.03.13-.02L6 .07h.06L6.3.03h.14A3.85 3.85 0 017 0zm1.88 1.3L7.44 2.45a.7.7 0 01-.8.05l-.08-.05L5.12 1.3a6 6 0 00-2.96 2.16l.65 1.72a.7.7 0 01-.2.78L2.54 6 1 7.02v.2a5.96 5.96 0 001.14 3.29l1.83-.09a.7.7 0 01.68.43l.03.09.49 1.77a5.94 5.94 0 003.66 0l.49-1.77a.7.7 0 01.62-.51h.09l1.84.08A5.96 5.96 0 0013 7.02l-1.54-1.01a.7.7 0 01-.3-.75l.03-.08.65-1.72A6.01 6.01 0 008.88 1.3zM7.4 4.5l1.84 1.33c.24.18.35.5.25.79l-.7 2.16a.7.7 0 01-.66.48H5.86a.7.7 0 01-.66-.48l-.7-2.16a.7.7 0 01.25-.78L6.59 4.5a.7.7 0 01.82 0z"
                />
            </g>
        </svg>
        {count && count > 1 && <span className="text-[8px] md:text-[9px] font-bold text-gray-800 mx-1">{count}</span>}
    </span>
);

/**
 * Yellow card icon
 */
export const YellowCardIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
    <span
        className={`inline-block w-2.5 h-3.5 md:w-3 md:h-4 bg-yellow-400 rounded-xs shadow-sm border border-yellow-500 ${className}`}
    />
);

/**
 * Red card icon
 */
export const RedCardIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
    <span
        className={`inline-block w-2.5 h-3.5 md:w-3 md:h-4 bg-red-500 rounded-xs shadow-sm border border-red-600 ${className}`}
    />
);

/**
 * Substitution out icon - red circle with white arrow pointing down
 */
export const SubOutIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
    <svg className={`w-3.5 h-3.5 md:w-4 md:h-4 ${className}`} viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="7" fill="#df2357" />
        <path
            fill="white"
            d="M9 3.5V7h1.44a.25.25 0 01.19.42l-3.26 3.66a.5.5 0 01-.74 0L3.37 7.42A.25.25 0 013.56 7H5V3.5c0-.28.22-.5.5-.5h3c.28 0 .5.22.5.5z"
        />
    </svg>
);

/**
 * Substitution in icon - green circle with white arrow pointing up
 */
export const SubInIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
    <svg className={`w-3.5 h-3.5 md:w-4 md:h-4 ${className}`} viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="7" fill="#22c55e" />
        <path
            fill="white"
            d="M5 10.5V7H3.56a.25.25 0 01-.19-.42l3.26-3.66a.5.5 0 01.74 0l3.26 3.66a.25.25 0 01-.19.42H9v3.5c0 .28-.22.5-.5.5h-3a.5.5 0 01-.5-.5z"
        />
    </svg>
);

/**
 * Soccer ball icon - simple version for use in stats/UI
 */
export const SoccerBallIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
            <circle cx="7" cy="7" r="6" fill="currentColor" opacity="0.15" />
            <path
                fill="currentColor"
                d="M7 0h.28H7a6.8 6.8 0 01.56.02l.14.01.25.03h.02l.04.01.22.04.13.02.14.03.13.03.24.06.08.03.23.07.04.01.53.2.03.01c.95.42 1.8 1.03 2.47 1.8a5.96 5.96 0 01.62.82l.08.13.06.1c.42.7.72 1.49.87 2.32l.02.07a7.47 7.47 0 01.07 1.85 6.97 6.97 0 01-.94 2.9l-.03.05-.08.13.1-.18-.2.34-.03.04-.37.5-.04.03c-.12.16-.26.3-.4.45l-.05.05-.12.12-.4.35-.07.05-.19.15-.04.02v.01l-.25.17.25-.18a7.3 7.3 0 01-1.67.9l-.05.02-.22.08-.06.02a5.9 5.9 0 01-.37.1l-.16.04a6.95 6.95 0 01-3.11.01l-.06-.01-.15-.04-.09-.02-.03-.01a6.16 6.16 0 01-.24-.07l-.09-.03-.2-.07-.06-.02a7.96 7.96 0 01-.24-.1h-.03c-.5-.22-.96-.48-1.38-.79h-.01l-.04-.03-.2-.16.24.18a6.66 6.66 0 01-.82-.7l-.05-.04a6.47 6.47 0 01-.4-.45l-.04-.04A6.97 6.97 0 01.03 7.66a7.5 7.5 0 010-1.34l.02-.13.01-.11.04-.27.02-.11c.16-.82.45-1.59.87-2.28l.06-.1.08-.13A6.94 6.94 0 014.22.58l.04-.02.51-.2.06-.01.23-.08.06-.01.25-.07c.05 0 .09-.02.13-.03l.14-.03.13-.02L6 .07h.06L6.3.03h.14A3.85 3.85 0 017 0zm1.88 1.3L7.44 2.45a.7.7 0 01-.8.05l-.08-.05L5.12 1.3a6 6 0 00-2.96 2.16l.65 1.72a.7.7 0 01-.2.78L2.54 6 1 7.02v.2a5.96 5.96 0 001.14 3.29l1.83-.09a.7.7 0 01.68.43l.03.09.49 1.77a5.94 5.94 0 003.66 0l.49-1.77a.7.7 0 01.62-.51h.09l1.84.08A5.96 5.96 0 0013 7.02l-1.54-1.01a.7.7 0 01-.3-.75l.03-.08.65-1.72A6.01 6.01 0 008.88 1.3zM7.4 4.5l1.84 1.33c.24.18.35.5.25.79l-.7 2.16a.7.7 0 01-.66.48H5.86a.7.7 0 01-.66-.48l-.7-2.16a.7.7 0 01.25-.78L6.59 4.5a.7.7 0 01.82 0z"
            />
        </g>
    </svg>
);
